import { Link } from "react-router-dom";
import type { PaginatedResponse, FeedbackResponse } from "@snaptics/api/src/types";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Camera, Video } from "lucide-react";

interface FeedbackListProps {
  data: PaginatedResponse<FeedbackResponse>;
  loadMoreUrl?: string;
}

export function FeedbackList({ data, loadMoreUrl }: FeedbackListProps) {
  const { items, pagination } = data;

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No feedback found.</p>
      </div>
    );
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "done":
        return "secondary";
      case "in_progress":
        return "default";
      case "planned":
        return "outline";
      default:
        return "outline";
    }
  };

  const getTypeBadgeVariant = (type: string) => {
    return type === "bug" ? "destructive" : "default";
  };

  return (
    <div className="space-y-4">
      {/* Table Container with professional styling */}
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="min-w-[280px] sm:w-[50%] font-semibold text-foreground">
                  Feedback
                </TableHead>
                <TableHead className="font-semibold text-foreground whitespace-nowrap">Type</TableHead>
                <TableHead className="font-semibold text-foreground whitespace-nowrap">Status</TableHead>
                <TableHead className="hidden sm:table-cell font-semibold text-foreground whitespace-nowrap">Submitted</TableHead>
                <TableHead className="w-[60px] font-semibold text-foreground text-center">Media</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((feedback) => (
                <TableRow
                  key={feedback.id}
                  className="group cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => {
                    // Navigation will be handled by the Link wrapper
                  }}
                >
                  <TableCell className="py-4">
                    <Link
                      to={`/feedback/${feedback.id}`}
                      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                    >
                      <div className="space-y-1">
                        <h3 className="font-semibold text-base leading-snug group-hover:text-primary transition-colors">
                          {feedback.title}
                        </h3>
                        {feedback.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                            {feedback.description}
                          </p>
                        )}
                        {/* Show date on mobile (when Submitted column is hidden) */}
                        <p className="text-xs text-muted-foreground font-medium pt-0.5 sm:hidden">
                          {formatDate(feedback.created_at)}
                        </p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="py-4">
                    <Badge variant={getTypeBadgeVariant(feedback.type)} className="font-medium whitespace-nowrap">
                      {feedback.type === "bug" ? "Bug" : "Idea"}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-4">
                    <Badge
                      variant={getStatusBadgeVariant(feedback.status)}
                      className="font-medium capitalize whitespace-nowrap"
                    >
                      {feedback.status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell py-4">
                    <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">
                      {formatDate(feedback.created_at)}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex gap-2 items-center justify-center">
                      {feedback.screenshot_url && (
                        <Camera
                          className="h-4 w-4 text-muted-foreground"
                          aria-label="Has screenshot"
                        />
                      )}
                      {feedback.video_url && (
                        <Video
                          className="h-4 w-4 text-muted-foreground"
                          aria-label="Has video"
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination footer with improved spacing and alignment */}
      <div className="flex items-center justify-between pt-2">
        <p className="text-sm font-medium text-muted-foreground">
          Showing{" "}
          <span className="text-foreground">{pagination.offset + 1}</span> to{" "}
          <span className="text-foreground">
            {Math.min(pagination.offset + pagination.limit, pagination.total)}
          </span>{" "}
          of <span className="text-foreground">{pagination.total}</span> results
        </p>

        {pagination.hasMore && loadMoreUrl && (
          <Button variant="outline" asChild>
            <Link to={loadMoreUrl}>Load More</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
